/**
 * Simple Video Overlay Component
 * Creates a black overlay that covers the entire video and fades away after 5 seconds
 */

class VideoOverlay {
  constructor(videoElement, options = {}) {
    this.videoElement = videoElement;
    this.options = {
      duration: 5000, // 5 seconds
      fadeDuration: 1000, // 1 second fade
      ...options,
    };

    this.overlay = null;
    this.isActive = false;
    this.init();
  }

  init() {
    this.createOverlay();
    this.startOverlay();
  }

  createOverlay() {
    // Create overlay element
    this.overlay = document.createElement("div");
    this.overlay.className = "video-overlay";
    this.overlay.style.cssText = `
      position: absolute;
      top: 0;
      left: 0;
      width: 100%;
      height: 100%;
      background-color: #000;
      z-index: 10;
      pointer-events: none;
      opacity: 1;
      transition: opacity ${this.options.fadeDuration}ms ease-out;
    `;

    // Insert overlay into the video container
    const container = this.videoElement.closest(".floating-video-player__video-container, .hero-video__video-wrapper, .hero-video__media-wrapper");
    if (container) {
      container.style.position = "relative";
      container.appendChild(this.overlay);
    }
  }

  startOverlay() {
    if (this.isActive) return;
    this.isActive = true;

    // Reset overlay opacity
    this.overlay.style.opacity = "1";

    // Start fade after duration
    setTimeout(() => {
      this.fadeOut();
    }, this.options.duration);
  }

  fadeOut() {
    if (!this.overlay) return;

    this.overlay.style.opacity = "0";

    // Remove overlay after fade completes
    setTimeout(() => {
      if (this.overlay && this.overlay.parentNode) {
        this.overlay.parentNode.removeChild(this.overlay);
      }
      this.isActive = false;
    }, this.options.fadeDuration);
  }

  destroy() {
    if (this.overlay && this.overlay.parentNode) {
      this.overlay.parentNode.removeChild(this.overlay);
    }
    this.isActive = false;
  }
}

// Auto-initialize for floating video players
document.addEventListener("DOMContentLoaded", () => {
  // Initialize for floating video players
  const floatingVideos = document.querySelectorAll("floating-video-player video, floating-video-player iframe");
  floatingVideos.forEach((video) => {
    new VideoOverlay(video);
  });

  // Initialize for hero videos (no delay - fade immediately)
  const heroVideos = document.querySelectorAll(".hero-video video, .hero-video iframe");
  heroVideos.forEach((video) => {
    new VideoOverlay(video, { duration: 0 });
  });
});

// Re-initialize when new floating video players are added
const observer = new MutationObserver((mutations) => {
  mutations.forEach((mutation) => {
    mutation.addedNodes.forEach((node) => {
      if (node.nodeType === Node.ELEMENT_NODE) {
        // Check for new floating video players
        if (node.tagName === "FLOATING-VIDEO-PLAYER") {
          const videos = node.querySelectorAll("video, iframe");
          videos.forEach((video) => {
            new VideoOverlay(video);
          });
        }

        // Check for new hero videos (no delay - fade immediately)
        if (node.classList && node.classList.contains("hero-video")) {
          const videos = node.querySelectorAll("video, iframe");
          videos.forEach((video) => {
            new VideoOverlay(video, { duration: 0 });
          });
        }
      }
    });
  });
});

observer.observe(document.body, {
  childList: true,
  subtree: true,
});

export { VideoOverlay };
